import { Remark } from "@skv/react-remark";
import * as React from "react";
import {
  Accordion as BootstrapAccordion,
  AccordionProps as BootstrapAccordionProps,
} from "react-bootstrap";
import styled from "styled-components";
import { SvgSprite } from "./SvgSprite";

const Toggle = styled.button`
  position: relative;
  top: 1px;
  margin-top: -1px;
  display: block;
  text-align: left;
  width: 100%;
  background-color: transparent;
  border: 0;
  padding: 0.6875rem 0;

  display: flex;

  border-top: 1px solid ${(p): string => p.theme.gray300};
  border-bottom: 1px solid ${(p): string => p.theme.gray300};

  svg {
    color: ${(p): string => p.theme.gray600};
    transition: transform 0.3s ${(p): string => p.theme.timingFnEaseOut};
  }
`;

interface CustomToggleProps {
  label: string;
  setActiveKey: (key: string) => void;
  active: boolean;
  eventKey: string;
}

const CustomToggle = (props: CustomToggleProps) => {
  const updateActiveKey = () => {
    props.setActiveKey(props.active ? "" : props.eventKey);
  };

  return (
    <Toggle
      className={props.active ? "open" : ""}
      css={`
        &.open {
          border-bottom-color: transparent;
        }
      `}
      type="button"
      aria-label={`Toggle ${props.label}`}
      aria-expanded={props.active ? "true" : "false"}
      onClick={updateActiveKey}
    >
      <span
        className="text-dark fw-bold"
        css={`
          font-size: 1.125rem;
          flex: 1 1 auto;
          padding-right: 3rem;
        `}
      >
        {props.label}
      </span>
      <SvgSprite
        id="icons:chev-down"
        height={16}
        width={16}
        css={`
          flex: 0 0 auto;
          transform: rotate3d(1, 0, 0, 0deg);

          .open & {
            transform: rotate3d(1, 0, 0, 180deg);
          }
        `}
      />
    </Toggle>
  );
};

export interface Question {
  question: string;
  answer: React.ReactNode;
}

interface AccordionProps extends BootstrapAccordionProps {
  questions: Question[];
  // answers can come from a json with html markup,
  // or also as a React.ReactNode
  answersAreHtml?: boolean; // todo: deprecate
  answersAreJsx?: boolean;
}

export const Accordion = ({
  questions,
  answersAreHtml = false,
  answersAreJsx = false,
  ...rest
}: AccordionProps) => {
  const [activeKey, setActiveKey] = React.useState<string>("");

  return (
    <BootstrapAccordion activeKey={activeKey} {...rest}>
      {questions.map((q, idx) => (
        <div key={idx}>
          <CustomToggle
            setActiveKey={setActiveKey}
            active={activeKey === `${idx}`}
            eventKey={`${idx}`}
            label={q.question}
          />
          <BootstrapAccordion.Collapse eventKey={`${idx}`}>
            {((): JSX.Element => {
              if (answersAreHtml) {
                return (
                  <div
                    dangerouslySetInnerHTML={{ __html: q.answer as string }}
                  />
                );
              }

              if (answersAreJsx) {
                return <Remark>{q.answer as string}</Remark>;
              }

              return <div>{q.answer}</div>;
            })()}
          </BootstrapAccordion.Collapse>
        </div>
      ))}
    </BootstrapAccordion>
  );
};
